import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { api, completeGoogleOAuth, verifyMagicLink } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { identifyUser, trackEvent } from "../lib/analytics";

// Handles the redirect back from any auth provider.
// - /auth/callback/google?code=...&state=... -> POST to /api/auth/google/callback
// - /auth/callback/email?token=...            -> POST to /api/auth/magic-link/verify
// - / with #session_id=...                    -> legacy Emergent-managed flow (kept for existing users)
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { provider } = useParams();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const params = new URLSearchParams(location.search);
    const hash = window.location.hash || "";
    const legacyMatch = hash.match(/session_id=([^&]+)/);

    const finish = (data, method) => {
      setUser(data);
      identifyUser(data);
      trackEvent(data?.is_new_user ? "sign_up" : "login", { method });
      window.history.replaceState(null, "", "/");
      navigate("/", { replace: true });
    };

    const fail = (msg) => {
      setError(msg || "Sign-in failed. Please try again.");
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    };

    const run = async () => {
      try {
        if (provider === "google") {
          const code = params.get("code");
          const state = params.get("state");
          const oauthError = params.get("error");
          if (oauthError) return fail(params.get("error_description") || oauthError);
          if (!code || !state) return fail("Missing Google authorization code.");
          const data = await completeGoogleOAuth(code, state);
          return finish(data, "google");
        }
        if (provider === "email") {
          const token = params.get("token");
          if (!token) return fail("Missing sign-in token.");
          const data = await verifyMagicLink(token);
          return finish(data, "email");
        }
        // Legacy path: Emergent-managed hash fragment (still supported for grandfathered sessions).
        if (legacyMatch) {
          const res = await api.post("/auth/session", { session_id: legacyMatch[1] });
          return finish(res.data, "emergent-google");
        }
        navigate("/login", { replace: true });
      } catch (err) {
        fail(err?.response?.data?.detail);
      }
    };
    run();
  }, [navigate, setUser, provider, location.search]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF9F5]">
      <div className="text-center">
        {error ? (
          <>
            <p className="text-sm font-semibold text-red-700">{error}</p>
            <p className="mt-2 text-xs text-neutral-500">Redirecting you back to sign in…</p>
          </>
        ) : (
          <p className="text-sm text-neutral-500">Signing you in…</p>
        )}
      </div>
    </div>
  );
}
